import { useState, useEffect, useCallback } from 'react';
import type { TourStep } from '@/components/ui/product-tour';

const TOUR_STORAGE_KEY = 'transaction-history-tour-completed';

export function useTransactionHistoryTour() {
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [hasCompletedTour, setHasCompletedTour] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem(TOUR_STORAGE_KEY);
    setHasCompletedTour(completed === 'true');
  }, []);

  const startTour = useCallback(() => {
    setIsTourOpen(true);
  }, []);

  const closeTour = useCallback(() => {
    setIsTourOpen(false);
  }, []);

  const completeTour = useCallback(() => {
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    setHasCompletedTour(true);
    setIsTourOpen(false);
  }, []);

  const resetTour = useCallback(() => {
    localStorage.removeItem(TOUR_STORAGE_KEY);
    setHasCompletedTour(false);
  }, []);

  const tourSteps: TourStep[] = [
    {
      target: '[data-tour="transaction-history-header"]',
      title: 'Welcome to Transaction History',
      content: 'This is your comprehensive view of all asset transactions. Here you can search, filter, sort, and analyze every transaction that has occurred across your asset portfolio.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="search-input"]',
      title: 'Search Transactions',
      content: 'Quickly find specific transactions by searching for asset codes, descriptions, references, or any other transaction details. The search filters results in real-time.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="transaction-table"]',
      title: 'Transaction Table',
      content: 'View all your transactions in a detailed table format. Each row represents a single transaction with comprehensive information including dates, values, and status.',
      placement: 'top',
    },
    {
      target: '[data-tour="select-checkbox-input"]',
      title: 'Select Transactions',
      content: 'Use checkboxes to select one or multiple transactions. Selecting transactions enables bulk actions like exporting data or generating reports.',
      placement: 'right',
    },
    {
      target: '[data-tour="asset-link"]',
      title: 'View Asset Details',
      content: 'Click on any asset code or description to navigate directly to that asset\'s detail page. This provides quick access to full asset information.',
      placement: 'right',
    },
    {
      target: '[data-tour="transaction-type"]',
      title: 'Transaction Types',
      content: 'Each transaction is color-coded by type: Additions (blue), Depreciation (amber), Disposal (red), Transfer (purple), and Revaluation (green). This makes it easy to identify transaction categories at a glance.',
      placement: 'left',
    },
    {
      target: '[data-tour="pagination"]',
      title: 'Navigate Pages',
      content: 'Use pagination controls to browse through large sets of transactions. You can see how many transactions are displayed and navigate between pages.',
      placement: 'top',
    },
  ];

  return {
    isTourOpen,
    hasCompletedTour,
    tourSteps,
    startTour,
    closeTour,
    completeTour,
    resetTour,
  };
}
